'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactSectionProps {
  className?: string;
}

const ContactSection = ({ className = '' }: ContactSectionProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    destination: '',
    budget: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactMethods = [
    {
      icon: 'PhoneIcon',
      title: '24/7 Phone Support',
      detail: '020 4615 2876',
      description: 'Speak with UK-based travel advisors',
    },
    {
      icon: 'EnvelopeIcon',
      title: 'Email Support',
      detail: 'info@dialforflight.co.uk',
      description: 'Response within 2 hours',
    },
    {
      icon: 'ChatBubbleLeftRightIcon',
      title: 'Live Chat',
      detail: 'AI-Powered Chat',
      description: 'Instant responses 24/7',
    },
    {
      icon: 'DevicePhoneMobileIcon',
      title: 'WhatsApp',
      detail: '020 4615 2876',
      description: 'Quick inquiries via WhatsApp',
    },
  ];

  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-br from-white via-surface to-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-4">
            Get in Touch with Our UK Team
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have questions? Our UK-based travel advisors are ready to help you plan your perfect journey
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-3d p-8 border-2 border-border">
            <h3 className="font-heading text-2xl font-black text-foreground mb-6">
              Request a Callback
            </h3>
            {submitted ? (
              <div className="bg-trust-green/10 border-2 border-trust-green rounded-lg p-6 text-center">
                <Icon name="CheckCircleIcon" size={48} className="text-trust-green mx-auto mb-4" />
                <p className="font-bold text-foreground">Thank you! We'll contact you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Destination
                  </label>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="e.g., Dubai, Spain, USA"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Budget (GBP)
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option value="">Select budget range</option>
                    <option value="under-500">Under £500</option>
                    <option value="500-1000">£500 - £1,000</option>
                    <option value="1000-2000">£1,000 - £2,000</option>
                    <option value="2000-5000">£2,000 - £5,000</option>
                    <option value="over-5000">Over £5,000</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                    placeholder="Tell us about your travel plans..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-4 gradient-gold-shiny text-white font-heading font-black rounded-lg shadow-3d border-2 border-accent transition-all duration-300 hover:scale-105 active:scale-95 shine-effect"
                >
                  Submit Request
                </button>
              </form>
            )}
          </div>

          {/* Contact Methods */}
          <div className="space-y-6">
            <h3 className="font-heading text-2xl font-black text-foreground mb-6">
              Multiple Ways to Reach Us
            </h3>
            {contactMethods.map((method, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-brand-lg hover:shadow-3d transition-all duration-300 p-6 border-2 border-border hover:border-primary/30 transform hover:-translate-y-1"
              >
                <div className="flex items-start gap-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex-shrink-0">
                    <Icon name={method.icon as any} size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-heading text-lg font-black text-foreground mb-1">
                      {method.title}
                    </h4>
                    <p className="text-primary font-bold mb-1">{method.detail}</p>
                    <p className="text-sm text-muted-foreground">{method.description}</p>
                    {method.icon === 'PhoneIcon' && (
                      <a
                        href="tel:02046152876"
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-gradient-to-r from-trust-green to-emerald-600 text-white text-sm font-bold rounded-lg hover:scale-105 transition-all duration-200 shadow-md"
                      >
                        <Icon name="PhoneIcon" size={16} className="text-white" />
                        Call Now
                      </a>
                    )}
                    {method.icon === 'EnvelopeIcon' && (
                      <a
                        href="mailto:info@dialforflight.co.uk"
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white text-sm font-bold rounded-lg hover:scale-105 transition-all duration-200 shadow-md"
                      >
                        <Icon name="EnvelopeIcon" size={16} className="text-white" />
                        Send Email
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Office Location */}
            <div className="bg-gradient-to-br from-primary via-secondary to-ocean rounded-xl shadow-3d p-6 text-white mt-8">
              <div className="flex items-start gap-4">
                <Icon name="MapPinIcon" size={32} className="text-accent flex-shrink-0" />
                <div>
                  <h4 className="font-heading text-xl font-black mb-2">UK Headquarters</h4>
                  <p className="text-white/90 mb-2">
                    123 Travel Innovation Hub<br />
                    London, EC2A 4NE<br />
                    United Kingdom
                  </p>
                  <p className="text-sm text-white/80">
                    Open Monday - Friday: 9:00 AM - 6:00 PM GMT<br />
                    Weekend support available via chat
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;